import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Warehouse, ChevronDown, Check, X, Loader2, MapPin } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { cn } from "../lib/utils";

// Match the DASH_BTN sizing used by every other action button + badge in
// the topbar (defined in Topbar.jsx). Kept as a plain string here rather
// than a shared export to avoid a circular import — the value is
// stable and one line.
const DASH_BTN = "w-44 h-10 justify-center";

/**
 * Dashboard "Yard Confirm" pill — surfaces jobs whose drop-off landed
 * inside a yard geofence but the app couldn't auto-start the storage
 * clock (unflagged yard, or edge-of-geofence drop). Storage backdating
 * is already handled on confirm — this pill is purely visibility so
 * nothing sits invisibly for days.
 *
 * Colour:
 *   amber → 1-2 waiting, all under 24h old
 *   red   → 3+ items OR any item older than 24h (money is leaking)
 *
 * Hidden entirely when count is 0.
 *
 * Sits in the topbar so it appears on every route (dashboard, yards,
 * history, impounds, settings…) — matching the "consistent topbar"
 * behaviour just shipped.
 */
export default function YardConfirmPill() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [anyStale, setAnyStale] = useState(false);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs/storage-suggestions/pending");
      setCount(data?.count || 0);
      setItems(data?.items || []);
      setAnyStale((data?.items || []).some((i) => (i.hours_waiting || 0) >= 24));
    } catch {
      // silent — not critical for the header to break if this fails
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);          // gentle refresh
    const onJobs = () => refresh();
    window.addEventListener("jobs:refresh", onJobs);
    return () => { clearInterval(t); window.removeEventListener("jobs:refresh", onJobs); };
  }, [refresh]);

  if (count === 0) return null;

  const isRed = count >= 3 || anyStale;
  const colourClasses = isRed
    ? "bg-rose-600 hover:bg-rose-700 text-white border-rose-700"
    : "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white border-amber-600";

  const runAction = async (jobId, action) => {
    setBusyId(`${jobId}-${action}`);
    try {
      await api.post(`/jobs/${jobId}/storage/${action}`);
      toast.success(action === "confirm"
        ? "Storage clock started (backdated to drop-off)"
        : "Suggestion declined — no clock started");
      await refresh();
      // Nudge the rest of the app to redraw affected job cards.
      window.dispatchEvent(new CustomEvent("jobs:refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-md border font-bold text-[10px] sm:text-xs uppercase tracking-wider transition-colors shrink-0",
            DASH_BTN,
            colourClasses,
            isRed && "animate-pulse",
          )}
          title={`${count} yard storage suggestion${count === 1 ? "" : "s"} awaiting your confirm/decline`}
          data-testid="yard-confirm-pill"
        >
          <Warehouse className="h-3.5 w-3.5 shrink-0" />
          <span>{count} Yard Confirm{count === 1 ? "" : "s"}</span>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] p-0" data-testid="yard-confirm-popover">
        <div className="p-3 border-b border-slate-200 dark:border-slate-700">
          <div className="font-display font-black text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Warehouse className="h-4 w-4 text-blue-600" />
            Yard suggestions awaiting review
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Storage clock backdates to drop-off — no time is lost if you confirm late.
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-200 dark:divide-slate-800">
          {items.map((item) => {
            const hrs = item.hours_waiting || 0;
            const stale = hrs >= 24;
            const yardLabel = (item.storage_suggested_yard || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
            return (
              <div key={item.id} className="p-3 space-y-1.5" data-testid={`yard-suggestion-${item.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{item.rego || "—"}</span>
                      <span className="text-xs text-slate-500 truncate">{item.vehicle || item.customer_name || ""}</span>
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{yardLabel} · {item.drop_off_address || item.address || "no address"}</span>
                    </div>
                    <div className={cn(
                      "text-[10px] font-bold uppercase tracking-wider mt-1",
                      stale ? "text-rose-600 dark:text-rose-400" : "text-slate-500",
                    )}>
                      Waiting {hrs != null ? `${hrs < 1 ? `${Math.round(hrs * 60)}m` : hrs < 24 ? `${hrs.toFixed(1)}h` : `${Math.floor(hrs / 24)}d`}` : "—"}
                      {stale && " · overdue"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => runAction(item.id, "confirm")}
                    disabled={!!busyId}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white h-8 text-xs"
                    data-testid={`yard-confirm-${item.id}`}
                  >
                    {busyId === `${item.id}-confirm`
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Check className="h-3 w-3" />}
                    Start Clock
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runAction(item.id, "decline")}
                    disabled={!!busyId}
                    className="flex-1 h-8 text-xs"
                    data-testid={`yard-decline-${item.id}`}
                  >
                    {busyId === `${item.id}-decline`
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <X className="h-3 w-3" />}
                    Not Storage
                  </Button>
                </div>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">No pending suggestions.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
